/**
 * API волны W1 — оболочка HUB: «Создать / Нужна помощь?» (POST /wiki/hub/feedback),
 * бейджи меню (GET /wiki/hub/counts), подстрока главной (GET /wiki/hub/summary),
 * обучение моих точек (GET /wiki/hub/my-points-learning), обращения из HUB
 * (GET /wiki/hub/feedback, только чтение).
 *
 * Правила (план wk6 §4): lib/wikiApi.ts заморожен после фазы 0 — новые вызовы
 * и аддитивные DTO волна кладёт только в свой модуль. Клиент — `api` из
 * '../api'.
 *
 * Обращения: CRM-пользователь пишет в существующий POST /api/feedback (общая
 * cookie .brasol.ru, как голосование в новостях), сотрудник iiko — в прокси
 * POST /api/wiki/hub/feedback (у него нет CRM-аккаунта).
 */
import { api, ApiError } from '../api';

// ── Бейджи меню и главная ────────────────────────────────────────────

/** Счётчики пунктов меню; null — счётчик вьюеру не положен или не посчитался. */
export interface HubCountsDto {
  tasks: number | null;
  news: number | null;
  standards: number | null;
  reviews: number | null;
  freshness: number | null;
  questions: number | null;
}

export type HubAudience = 'uk' | 'partner' | 'manager' | 'employee';

export interface HubSummaryDto {
  audience: HubAudience;
  scope: 'network' | 'points' | 'point' | 'none';
  partners: number | null;
  points: number | null;
  learners: number | null;
  staff: number | null;
  pointName: string | null;
}

export interface HubPointLearningRow {
  id: string;
  /** «Город · Название». */
  name: string;
  staff: number;
  learners: number;
  assigned: number;
  completed: number;
  /** null — назначений нет. */
  pct: number | null;
  overdue: number;
  overdueLearners: number;
}

export interface HubPointsLearningDto {
  scope: 'network' | 'points' | 'point' | 'none';
  points: HubPointLearningRow[];
}

// ── Обращения (CRM «Обращения») ─────────────────────────────────────

/** Виды формы HUB: request уходит в CRM вопросом с разделом «HUB · Обращение». */
export type HubRequestKind = 'bug' | 'idea' | 'request';
/** Обычная / Высокая / Блокирует работу → minor / annoy / block (CRM). */
export type HubSeverity = 'normal' | 'high' | 'block';

export interface HubFeedbackInput {
  kind: HubRequestKind;
  title: string;
  body: string | null;
  severity: HubSeverity | null;
  /** Название страницы, откуда пишут («Диск», «Курс»). */
  page: string;
  pageUrl: string;
  /** Скриншот/файл: картинка или PDF до 10 МБ. */
  file: File | null;
}

export interface HubTicketDto {
  id: string;
  code: string;
  kind: 'bug' | 'idea' | 'question';
  kindLabel: string;
  status: string;
  statusLabel: string;
  title: string;
  section: string | null;
  severity: string | null;
  authorName: string | null;
  pointName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HubMyTicketDto {
  id: string;
  code: string;
  kind: 'bug' | 'idea' | 'question';
  kindLabel: string;
  status: string;
  statusLabel: string;
  title: string;
  rejectReason: string | null;
  lastReply: { text: string; at: string; authorName: string | null } | null;
  createdAt: string;
  updatedAt: string;
}

/** Раздел для доски CRM: «HUB · Обращение» / «HUB · <страница>» (как wiki-hub.rules на бэке). */
export function hubSection(kind: HubRequestKind, page: string): string {
  if (kind === 'request') return 'HUB · Обращение';
  const p = page.replace(/\s+/g, ' ').trim();
  return `HUB · ${p || 'Без раздела'}`.slice(0, 160);
}

/** Шкала CRM для «Насколько мешает» (только ошибка). */
export function crmSeverity(kind: HubRequestKind, sev: HubSeverity | null): 'minor' | 'annoy' | 'block' | null {
  if (kind !== 'bug') return null;
  return sev === 'block' ? 'block' : sev === 'high' ? 'annoy' : 'minor';
}

/** Технический контекст без ПДн (бэк его ещё раз чистит sanitizeContext). Им же пользуется плавающий «+» (components/feedback). */
export function clientContext(section: string, role: string | null): Record<string, string> {
  const nav = typeof navigator !== 'undefined' ? navigator : null;
  let timezone = '';
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* старый браузер */
  }
  return {
    version: 'HUB 2.0',
    section,
    ...(role ? { role } : {}),
    browser: nav?.userAgent.slice(0, 120) ?? '',
    language: nav?.language ?? '',
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : '',
    timezone,
  };
}

async function readError(r: Response): Promise<ApiError> {
  let parsed: unknown = null;
  try {
    parsed = await r.json();
  } catch {
    /* не JSON */
  }
  const msg =
    (parsed && typeof parsed === 'object' && 'message' in parsed ? String((parsed as { message: unknown }).message) : null) ||
    (r.status === 429 ? 'Слишком много обращений — попробуйте позже' : `HTTP ${r.status}`);
  return new ApiError(r.status, parsed, msg);
}

async function postForm<T>(path: string, form: FormData): Promise<T> {
  const r = await fetch(`/api${path}`, { method: 'POST', credentials: 'include', body: form });
  if (!r.ok) throw await readError(r);
  return (await r.json()) as T;
}

export const hubApi = {
  counts: () => api.get<HubCountsDto>('/wiki/hub/counts'),
  summary: () => api.get<HubSummaryDto>('/wiki/hub/summary'),
  myPointsLearning: () => api.get<HubPointsLearningDto>('/wiki/hub/my-points-learning'),
  /** Обращения из HUB (право appeals_feedback:view), только чтение. */
  feedback: (status: 'open' | 'all' = 'open') => api.get<HubTicketDto[]>(`/wiki/hub/feedback?status=${status}`),
  /** Мои обращения сотрудника iiko. */
  myFeedback: () => api.get<HubMyTicketDto[]>('/wiki/hub/feedback/mine'),

  /**
   * Отправить «Написать в УК». CRM-пользователь → POST /api/feedback (+ скриншот
   * отдельным POST /api/feedback/:id/attachments), сотрудник iiko → прокси HUB
   * одним multipart-запросом.
   */
  async sendFeedback(input: HubFeedbackInput, viewer: { type: 'crm_user' | 'iiko_employee'; roleName?: string | null }): Promise<{ id: string; code: string; attachError?: string }> {
    const section = hubSection(input.kind, input.page);
    const context = clientContext(section, viewer.roleName ?? null);
    if (viewer.type === 'iiko_employee') {
      const form = new FormData();
      form.append('kind', input.kind);
      form.append('title', input.title);
      if (input.body) form.append('body', input.body);
      if (input.severity) form.append('severity', input.severity);
      form.append('page', input.page);
      form.append('pageUrl', input.pageUrl);
      form.append('context', JSON.stringify(context));
      if (input.file) form.append('file', input.file);
      return postForm<{ id: string; code: string }>('/wiki/hub/feedback', form);
    }
    const created = await api.post<{ id: string; code: string }>('/feedback', {
      kind: input.kind === 'request' ? 'question' : input.kind,
      title: input.title,
      body: input.body,
      severity: crmSeverity(input.kind, input.severity),
      section,
      pageUrl: input.pageUrl,
      context,
    });
    if (!input.file) return created;
    // Обращение уже создано — неудачный скриншот не должен выглядеть как «не отправлено».
    try {
      const form = new FormData();
      form.append('file', input.file);
      form.append('kind', input.file.type.startsWith('image/') ? 'screenshot' : 'file');
      await postForm(`/feedback/${encodeURIComponent(created.id)}/attachments`, form);
      return created;
    } catch (e) {
      return { ...created, attachError: (e as Error).message };
    }
  },
};
